import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api, { Lane } from '../lib/api'
import { normalizeZipInput, useZipLocation } from '../lib/useZipLocation'

interface UnitMoveForm {
  vin: string
  year: string
  make: string
  model: string
  priority: string
  readyDate: string
  notes: string
}

const emptyUnitMove = (): UnitMoveForm => ({
  vin: '',
  year: '',
  make: '',
  model: '',
  priority: 'NORMAL',
  readyDate: '',
  notes: '',
})

function normalizeVinInput(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17)
}

export default function CreateOrderPage() {
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [customers, setCustomers] = useState<Array<{ id: string; name: string; email?: string | null; phone?: string | null }>>([])
  const [places, setPlaces] = useState<Array<{ id: string; name?: string | null; city?: string | null; state?: string | null; zip?: string | null; placeType?: string | null }>>([])
  const [customerId, setCustomerId] = useState('')
  const [lanes, setLanes] = useState<Lane[]>([])
  const [selectedLaneId, setSelectedLaneId] = useState('')
  const [pickupPlaceId, setPickupPlaceId] = useState('')
  const [deliveryPlaceId, setDeliveryPlaceId] = useState('')
  const [hierarchyAccounts, setHierarchyAccounts] = useState<Array<{ id: string; legalName: string; displayName?: string | null }>>([])
  const [hierarchyLocations, setHierarchyLocations] = useState<Array<{ id: string; accountId: string; name: string; city?: string | null; state?: string | null }>>([])
  const [hierarchyContacts, setHierarchyContacts] = useState<Array<{ id: string; accountId: string; fullName: string; email?: string | null }>>([])
  const [hierarchyBillingProfiles, setHierarchyBillingProfiles] = useState<Array<{ id: string; accountId: string; profileName: string }>>([])
  const [accountHierarchyId, setAccountHierarchyId] = useState('')
  const [locationHierarchyId, setLocationHierarchyId] = useState('')
  const [contactHierarchyId, setContactHierarchyId] = useState('')
  const [billingProfileId, setBillingProfileId] = useState('')

  const [customerName, setCustomerName] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [paymentTerms, setPaymentTerms] = useState('NET 30')

  const [pickupCity, setPickupCity] = useState('')
  const [pickupState, setPickupState] = useState('')
  const [pickupZip, setPickupZip] = useState('')

  const [deliveryCity, setDeliveryCity] = useState('')
  const [deliveryState, setDeliveryState] = useState('')
  const [deliveryZip, setDeliveryZip] = useState('')

  const [customerPrice, setCustomerPrice] = useState('')
  const [carrierCost, setCarrierCost] = useState('')
  const [readyDate, setReadyDate] = useState('')
  const [pickupWindowStart, setPickupWindowStart] = useState('')
  const [pickupWindowEnd, setPickupWindowEnd] = useState('')
  const [deliveryWindowStart, setDeliveryWindowStart] = useState('')
  const [deliveryWindowEnd, setDeliveryWindowEnd] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [notes, setNotes] = useState('')

  const [unitMoves, setUnitMoves] = useState<UnitMoveForm[]>([emptyUnitMove()])

  const pickupZipLookup = useZipLocation({ zip: pickupZip, onCity: setPickupCity, onState: setPickupState, lookupZip: api.geo.zipLookup })
  const deliveryZipLookup = useZipLocation({ zip: deliveryZip, onCity: setDeliveryCity, onState: setDeliveryState, lookupZip: api.geo.zipLookup })

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [customersRes, placesRes, lanesRes] = await Promise.all([
          api.customers.list(),
          api.places.list(),
          api.lanes.list({ status: 'ACTIVE' }),
        ])
        setCustomers(customersRes.data.map((c) => ({ id: c.id, name: c.name, email: c.email, phone: c.phone })))
        setPlaces(placesRes.data.map((p) => ({
          id: p.id,
          name: p.name,
          city: p.city,
          state: p.state,
          zip: p.zip,
          placeType: p.placeType,
        })))
        setLanes(lanesRes.data)
        const hierarchyRes = await api.accountHierarchy.search()
        setHierarchyAccounts(hierarchyRes.data.accounts.map((item) => ({ id: item.id, legalName: item.legalName, displayName: item.displayName })))
        setHierarchyLocations(hierarchyRes.data.locations.map((item) => ({ id: item.id, accountId: item.accountId, name: item.name, city: item.city, state: item.state })))
        setHierarchyContacts(hierarchyRes.data.contacts.map((item) => ({ id: item.id, accountId: item.accountId, fullName: item.fullName, email: item.email })))
        setHierarchyBillingProfiles(hierarchyRes.data.billingProfiles.map((item) => ({ id: item.id, accountId: item.accountId, profileName: item.profileName })))
      } catch {
        // Lookup hydration is best effort; manual entry remains available.
      }
    }
    void loadLookups()
  }, [])

  const handleLanePrefill = (laneId: string) => {
    setSelectedLaneId(laneId)
    if (!laneId) return
    const lane = lanes.find((entry) => entry.id === laneId)
    if (!lane) return

    if (lane.customerId) setCustomerId(lane.customerId)
    if (lane.customerName) setCustomerName(lane.customerName)
    if (lane.originCity) setPickupCity(lane.originCity)
    if (lane.originState) setPickupState(lane.originState)
    if (lane.originZip) setPickupZip(lane.originZip)
    if (lane.destinationCity) setDeliveryCity(lane.destinationCity)
    if (lane.destinationState) setDeliveryState(lane.destinationState)
    if (lane.destinationZip) setDeliveryZip(lane.destinationZip)
    if (lane.targetCustomerPayCents != null) setCustomerPrice((lane.targetCustomerPayCents / 100).toFixed(2))
    if (lane.targetCarrierPayCents != null) setCarrierCost((lane.targetCarrierPayCents / 100).toFixed(2))
    if (lane.notes && !notes) setNotes(lane.notes)
  }

  const updateUnitMove = (index: number, patch: Partial<UnitMoveForm>) => {
    setUnitMoves((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const nonEmptyUnitMoves = unitMoves.filter((row) => normalizeVinInput(row.vin).length > 0)

  const decodeVin = async (index: number) => {
    const vin = normalizeVinInput(unitMoves[index].vin)
    if (vin.length !== 17) return
    updateUnitMove(index, { vin })
    try {
      const response = await api.vehicles.decodeVin(vin)
      const decoded = response.data
      if (!decoded.valid) return
      updateUnitMove(index, {
        year: decoded.year ?? unitMoves[index].year,
        make: decoded.make ?? unitMoves[index].make,
        model: decoded.model ?? unitMoves[index].model,
      })
    } catch {
      // VIN decode is best-effort only in form.
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!customerName.trim()) {
      setError('Customer name is required.')
      return
    }
    if (!pickupZip.trim() || !deliveryZip.trim()) {
      setError('Pickup and delivery ZIP are required.')
      return
    }
    if (!nonEmptyUnitMoves.length) {
      setError('Add at least one VIN before saving the order.')
      return
    }
    if (nonEmptyUnitMoves.some((item) => normalizeVinInput(item.vin).length !== 17)) {
      setError('Each unit move must include a valid 17-character VIN.')
      return
    }

    setSaving(true)
    try {
      const response = await api.orders.create({
        status: 'DRAFT',
        customerId: customerId || undefined,
        accountHierarchyId: accountHierarchyId || undefined,
        locationHierarchyId: locationHierarchyId || undefined,
        contactHierarchyId: contactHierarchyId || undefined,
        billingProfileId: billingProfileId || undefined,
        customer: {
          name: customerName,
          email: customerEmail || undefined,
          phone: customerPhone || undefined,
          paymentTerms: paymentTerms || undefined,
        },
        pickupPlaceId: pickupPlaceId || undefined,
        pickupPlace: {
          city: pickupCity || undefined,
          state: pickupState || undefined,
          zip: pickupZip || undefined,
        },
        deliveryPlaceId: deliveryPlaceId || undefined,
        deliveryPlace: {
          city: deliveryCity || undefined,
          state: deliveryState || undefined,
          zip: deliveryZip || undefined,
        },
        customerPriceCents: customerPrice ? Math.round(Number(customerPrice) * 100) : undefined,
        carrierCostCents: carrierCost ? Math.round(Number(carrierCost) * 100) : undefined,
        paymentTerms: paymentTerms || undefined,
        readyDate: readyDate || undefined,
        pickupWindowStart: pickupWindowStart || undefined,
        pickupWindowEnd: pickupWindowEnd || undefined,
        deliveryWindowStart: deliveryWindowStart || undefined,
        deliveryWindowEnd: deliveryWindowEnd || undefined,
        referenceNumber: referenceNumber || undefined,
        notes: notes || undefined,
        unitMoves: nonEmptyUnitMoves.map((item) => ({
          vin: normalizeVinInput(item.vin),
          year: item.year ? Number(item.year) : undefined,
          make: item.make || undefined,
          model: item.model || undefined,
          priority: item.priority,
          readyDate: item.readyDate || readyDate || undefined,
          notes: item.notes || undefined,
        })),
      })
      navigate(`/orders/${response.data.order.id}`)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to create order.')
    } finally {
      setSaving(false)
    }
  }

  const quickCreateCustomer = async () => {
    try {
      if (!customerName.trim()) {
        setError('Enter a customer name before quick-create.')
        return
      }
      const res = await api.customers.create({
        name: customerName,
        email: customerEmail || undefined,
        phone: customerPhone || undefined,
      })
      const created = res.data.customer
      setCustomers((prev) => [{ id: created.id, name: created.name, email: created.email, phone: created.phone }, ...prev])
      setCustomerId(created.id)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to quick-create customer.')
    }
  }

  const quickCreatePlace = async (type: 'PICKUP' | 'DELIVERY') => {
    try {
      const city = type === 'PICKUP' ? pickupCity : deliveryCity
      const state = type === 'PICKUP' ? pickupState : deliveryState
      const zip = type === 'PICKUP' ? pickupZip : deliveryZip
      const name = `${type} ${city || state || zip || 'PLACE'}`.trim()
      const res = await api.places.create({ placeType: type, name, city: city || undefined, state: state || undefined, zip: zip || undefined })
      const created = res.data.place
      setPlaces((prev) => [created as any, ...prev])
      if (type === 'PICKUP') setPickupPlaceId(created.id)
      else setDeliveryPlaceId(created.id)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? `Failed to quick-create ${type.toLowerCase()} place.`)
    }
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Create Order</h1>
        <p className="text-sm text-gray-500 mt-1">Build customer order records with one or many VIN unit moves.</p>
      </div>

      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <form onSubmit={submit} className="space-y-5">
        <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <h2 className="md:col-span-2 font-semibold">Lane Prefill</h2>
          <select className="border rounded px-3 py-2 text-sm md:col-span-2" value={selectedLaneId} onChange={(e) => handleLanePrefill(e.target.value)}>
            <option value="">Select lane (optional)</option>
            {lanes.map((lane) => (
              <option key={lane.id} value={lane.id}>
                {lane.laneNumber} · {lane.customerName ?? 'No customer'} · {lane.originZip ?? '-'} to {lane.destinationZip ?? '-'}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <h2 className="md:col-span-4 font-semibold">Customer</h2>
          <select
            className="border rounded px-3 py-2 text-sm"
            value={accountHierarchyId}
            onChange={(e) => {
              const nextAccountId = e.target.value
              setAccountHierarchyId(nextAccountId)
              setLocationHierarchyId('')
              setContactHierarchyId('')
              setBillingProfileId('')
            }}
          >
            <option value="">Select hierarchy account (optional)</option>
            {hierarchyAccounts.map((a) => (
              <option key={a.id} value={a.id}>{a.displayName || a.legalName}</option>
            ))}
          </select>
          <select className="border rounded px-3 py-2 text-sm" value={locationHierarchyId} onChange={(e) => setLocationHierarchyId(e.target.value)}>
            <option value="">Select account location (optional)</option>
            {hierarchyLocations.filter((l) => !accountHierarchyId || l.accountId === accountHierarchyId).map((l) => (
              <option key={l.id} value={l.id}>{l.name}{l.city ? ` · ${l.city}` : ''}{l.state ? `, ${l.state}` : ''}</option>
            ))}
          </select>
          <select className="border rounded px-3 py-2 text-sm" value={contactHierarchyId} onChange={(e) => setContactHierarchyId(e.target.value)}>
            <option value="">Select requestor contact (optional)</option>
            {hierarchyContacts.filter((c) => !accountHierarchyId || c.accountId === accountHierarchyId).map((c) => (
              <option key={c.id} value={c.id}>{c.fullName}{c.email ? ` · ${c.email}` : ''}</option>
            ))}
          </select>
          <select className="border rounded px-3 py-2 text-sm" value={billingProfileId} onChange={(e) => setBillingProfileId(e.target.value)}>
            <option value="">Select billing profile (optional)</option>
            {hierarchyBillingProfiles.filter((b) => !accountHierarchyId || b.accountId === accountHierarchyId).map((b) => (
              <option key={b.id} value={b.id}>{b.profileName}</option>
            ))}
          </select>
          <select
            className="border rounded px-3 py-2 text-sm"
            value={customerId}
            onChange={(e) => {
              const id = e.target.value
              setCustomerId(id)
              const found = customers.find((c) => c.id === id)
              if (!found) return
              setCustomerName(found.name ?? '')
              setCustomerEmail(found.email ?? '')
              setCustomerPhone(found.phone ?? '')
            }}
          >
            <option value="">Select existing customer (optional)</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <input className="border rounded px-3 py-2 text-sm" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name" />
          <input className="border rounded px-3 py-2 text-sm" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="Email" />
          <input className="border rounded px-3 py-2 text-sm" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Phone" />
          <input className="border rounded px-3 py-2 text-sm" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="Payment terms" />
          <div className="md:col-span-4">
            <button type="button" onClick={() => void quickCreateCustomer()} className="text-xs border rounded px-3 py-1">Quick-create customer from typed fields</button>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h2 className="font-semibold">Pickup Place</h2>
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={pickupPlaceId}
              onChange={(e) => {
                const id = e.target.value
                setPickupPlaceId(id)
                const found = places.find((p) => p.id === id)
                if (!found) return
                setPickupCity(found.city ?? '')
                setPickupState(found.state ?? '')
                setPickupZip(found.zip ?? '')
              }}
            >
              <option value="">Select existing pickup place (optional)</option>
              {places.map((p) => (
                <option key={p.id} value={p.id}>{p.name || `${p.city || ''}, ${p.state || ''}`.trim()}</option>
              ))}
            </select>
            <input className="w-full border rounded px-3 py-2 text-sm" value={pickupCity} onChange={(e) => setPickupCity(e.target.value)} placeholder="City" />
            <div className="grid grid-cols-2 gap-2">
              <input className="border rounded px-3 py-2 text-sm" value={pickupState} onChange={(e) => setPickupState(e.target.value)} placeholder="State" />
              <input className="border rounded px-3 py-2 text-sm" value={pickupZip} onChange={(e) => setPickupZip(normalizeZipInput(e.target.value))} placeholder="ZIP" maxLength={10} inputMode="numeric" />
              {pickupZipLookup.loading && <p className="text-xs text-slate-500" role="status">Looking up ZIP...</p>}
            </div>
            <button type="button" onClick={() => void quickCreatePlace('PICKUP')} className="text-xs border rounded px-3 py-1">Quick-create pickup place</button>
          </div>

          <div className="space-y-2">
            <h2 className="font-semibold">Delivery Place</h2>
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={deliveryPlaceId}
              onChange={(e) => {
                const id = e.target.value
                setDeliveryPlaceId(id)
                const found = places.find((p) => p.id === id)
                if (!found) return
                setDeliveryCity(found.city ?? '')
                setDeliveryState(found.state ?? '')
                setDeliveryZip(found.zip ?? '')
              }}
            >
              <option value="">Select existing delivery place (optional)</option>
              {places.map((p) => (
                <option key={p.id} value={p.id}>{p.name || `${p.city || ''}, ${p.state || ''}`.trim()}</option>
              ))}
            </select>
            <input className="w-full border rounded px-3 py-2 text-sm" value={deliveryCity} onChange={(e) => setDeliveryCity(e.target.value)} placeholder="City" />
            <div className="grid grid-cols-2 gap-2">
              <input className="border rounded px-3 py-2 text-sm" value={deliveryState} onChange={(e) => setDeliveryState(e.target.value)} placeholder="State" />
              <input className="border rounded px-3 py-2 text-sm" value={deliveryZip} onChange={(e) => setDeliveryZip(normalizeZipInput(e.target.value))} placeholder="ZIP" maxLength={10} inputMode="numeric" />
              {deliveryZipLookup.loading && <p className="text-xs text-slate-500" role="status">Looking up ZIP...</p>}
            </div>
            <button type="button" onClick={() => void quickCreatePlace('DELIVERY')} className="text-xs border rounded px-3 py-1">Quick-create delivery place</button>
          </div>
        </div>

        {(pickupZipLookup.message || deliveryZipLookup.message) && <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="status">{pickupZipLookup.message ?? deliveryZipLookup.message}</p>}

        <div className="bg-white border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">VIN / Unit Moves</h2>
            <button type="button" className="text-sm text-blue-700" onClick={() => setUnitMoves((prev) => [...prev, emptyUnitMove()])}>+ Add VIN</button>
          </div>

          {!!nonEmptyUnitMoves.length && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {nonEmptyUnitMoves.map((item, index) => (
                <div key={`preview-${index}-${item.vin}`} className="border rounded-lg px-3 py-2 bg-gray-50 text-sm">
                  <div className="font-medium">{normalizeVinInput(item.vin)}</div>
                  <div className="text-gray-600">{[item.year, item.make, item.model].filter(Boolean).join(' ') || 'Decoding pending'}</div>
                  <div className="text-xs text-gray-500">Last 6: {normalizeVinInput(item.vin).slice(-6)}</div>
                </div>
              ))}
            </div>
          )}

          {unitMoves.map((item, index) => (
            <div key={`${index}-${item.vin}`} className="border rounded-lg p-3 grid grid-cols-1 md:grid-cols-6 gap-2">
              <input
                className="md:col-span-2 border rounded px-3 py-2 text-sm"
                value={item.vin}
                onChange={(e) => updateUnitMove(index, { vin: normalizeVinInput(e.target.value) })}
                onBlur={() => void decodeVin(index)}
                placeholder="VIN"
              />
              <input className="border rounded px-3 py-2 text-sm" value={item.year} onChange={(e) => updateUnitMove(index, { year: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="Year" />
              <input className="border rounded px-3 py-2 text-sm" value={item.make} onChange={(e) => updateUnitMove(index, { make: e.target.value })} placeholder="Make" />
              <input className="border rounded px-3 py-2 text-sm" value={item.model} onChange={(e) => updateUnitMove(index, { model: e.target.value })} placeholder="Model" />
              <select className="border rounded px-3 py-2 text-sm" value={item.priority} onChange={(e) => updateUnitMove(index, { priority: e.target.value })}>
                <option value="LOW">LOW</option>
                <option value="NORMAL">NORMAL</option>
                <option value="HIGH">HIGH</option>
              </select>
              <input className="border rounded px-3 py-2 text-sm" type="date" value={item.readyDate} onChange={(e) => updateUnitMove(index, { readyDate: e.target.value })} />
              <input className="md:col-span-5 border rounded px-3 py-2 text-sm" value={item.notes} onChange={(e) => updateUnitMove(index, { notes: e.target.value })} placeholder="Unit move notes" />
              {unitMoves.length > 1 && (
                <button type="button" className="text-xs text-red-600" onClick={() => setUnitMoves((prev) => prev.filter((_, i) => i !== index))}>
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <h2 className="md:col-span-3 font-semibold">Pricing / Dates / Notes</h2>
          <input className="border rounded px-3 py-2 text-sm" value={customerPrice} onChange={(e) => setCustomerPrice(e.target.value)} placeholder="Customer price ($)" />
          <input className="border rounded px-3 py-2 text-sm" value={carrierCost} onChange={(e) => setCarrierCost(e.target.value)} placeholder="Carrier cost ($)" />
          <input className="border rounded px-3 py-2 text-sm" type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" type="datetime-local" value={pickupWindowStart} onChange={(e) => setPickupWindowStart(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" type="datetime-local" value={pickupWindowEnd} onChange={(e) => setPickupWindowEnd(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" type="datetime-local" value={deliveryWindowStart} onChange={(e) => setDeliveryWindowStart(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" type="datetime-local" value={deliveryWindowEnd} onChange={(e) => setDeliveryWindowEnd(e.target.value)} />
          <input className="border rounded px-3 py-2 text-sm" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="Reference number" />
          <textarea className="md:col-span-3 border rounded px-3 py-2 text-sm" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Order notes" />
        </div>

        <div className="flex gap-2">
          <button type="submit" disabled={saving} className="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-800 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Order'}
          </button>
          <button type="button" onClick={() => navigate('/orders')} className="px-4 py-2 rounded-lg border text-sm">Cancel</button>
        </div>
      </form>
    </div>
  )
}
